import { AccessTime as AccessTimeIcon, Download as DownloadIcon } from '@mui/icons-material';
import { Box, Chip, Divider, Menu, MenuItem, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Button, Panel } from 'src/UIKit';
import { getTimePassed } from 'src/constants/helpers';
import { handleDownload } from 'src/helpers/helpers';
import { useCampaignsApi } from 'src/modules/campaigns/api/CampaignsApi';
import { Message, SurveyExportFormat, SurveyReply } from 'src/modules/campaigns/types';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';

type QuestionResponsesViewProps = {
  questionNumber: number;
  totalQuestions: number;
  question?: Message;
  companyId?: string;
  startDateInterval?: string | null;
  endDateInterval?: string | null;
};

export const QuestionResponsesView = ({
  questionNumber,
  totalQuestions,
  question,
  companyId,
  startDateInterval,
  endDateInterval,
}: QuestionResponsesViewProps) => {
  const { getSurveyMessageRepliesExport: getSurveyMessageRepliesExport, getSurveyMessageReplies: getSurveyReplies } = useCampaignsApi();
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [replies, setReplies] = useState<SurveyReply[]>([]);
  const [totalReplies, setTotalReplies] = useState(0);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);
  const [repliesError, setRepliesError] = useState<string | null>(null);
  const scheduledMessageId = question?.id;

  const handleDownloadExport = async (format: SurveyExportFormat) => {
    if (!companyId || !scheduledMessageId) {
      return;
    }
    try {
      const response = await getSurveyMessageRepliesExport(companyId, scheduledMessageId, format, {
        startDateInterval,
        endDateInterval,
      });
      handleDownload(response);
    } catch (error) {
      reportErrorToUser(error, GENERIC_ERROR_MESSAGE);
    }
    setDownloadMenuAnchor(null);
  };

  useEffect(() => {
    if (!companyId || !scheduledMessageId) {
      setReplies([]);
      setTotalReplies(0);
      setRepliesError(null);
      return;
    }

    let isActive = true;
    setIsLoadingReplies(true);
    setRepliesError(null);

    getSurveyReplies({
      companyId,
      scheduledMessageId,
      limit: 50,
      offset: 0,
      startDateInterval,
      endDateInterval,
    })
      .then(response => {
        if (!isActive) {
          return;
        }
        setReplies(response.data.items ?? []);
        setTotalReplies(response.data.count ?? 0);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setRepliesError('Unable to load responses.');
        setTotalReplies(0);
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoadingReplies(false);
      });

    return () => {
      isActive = false;
    };
  }, [companyId, scheduledMessageId, getSurveyReplies, startDateInterval, endDateInterval]);

  return (
    <Panel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={`Question ${questionNumber} of ${Math.max(totalQuestions, questionNumber)}`}
          color="primary"
          size="small"
          variant="outlined"
        />
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={event => setDownloadMenuAnchor(event.currentTarget)}
            disabled={!companyId || !scheduledMessageId}
          >
            Export
          </Button>
        </Box>
      </Box>
      <Typography variant="body1" color="text.primary">
        {question?.text ?? 'Question details and responses will appear here.'}
      </Typography>
      {totalReplies > 1 && (
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {totalReplies} total responses
        </Typography>
      )}
      <Stack spacing={2} mt={2}>
        {isLoadingReplies && (
          <Typography variant="body2" color="text.secondary">
            Loading responses...
          </Typography>
        )}
        {repliesError && (
          <Typography variant="body2" color="error">
            {repliesError}
          </Typography>
        )}
        {!isLoadingReplies && !repliesError && replies.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No responses yet.
          </Typography>
        )}
        {replies.map(reply => {
          const contactName = [reply.contact.firstName, reply.contact.lastName]
            .filter(Boolean)
            .join(' ')
            .trim() || 'Unknown contact';
          const contactMeta = [reply.contact.phoneNumber, reply.contact.email]
            .filter(Boolean)
            .join(' • ');

          return (
            <Panel key={reply.id}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1" color="text.primary">
                    {contactName}
                  </Typography>
                  {contactMeta && (
                    <Typography variant="body2" color="text.secondary">
                      {contactMeta}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {getTimePassed(reply.dateCreated)} ago
                  </Typography>
                </Stack>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body1" color="text.primary">
                {reply.text || 'No response text.'}
              </Typography>
            </Panel>
          );
        })}
      </Stack>
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleDownloadExport('csv')}>Download Responses as CSV</MenuItem>
        <MenuItem onClick={() => handleDownloadExport('xlsx')}>Download Responses as XLSX</MenuItem>
      </Menu>
    </Panel>
  );
};
